import { Directive, ElementRef, Input, OnChanges, Optional, Renderer2, Self } from '@angular/core';
import { MatFormFieldControl } from '@angular/material/form-field';

/**
 * Exposes a form control's required state to assistive technology.
 *
 * Apply this to the control itself, not to its label. Putting an aria-label on a mat-label
 * replaces the label text in the accessible name computation, so the control ends up named
 * after the marker instead of the field (WCAG 2.1 SC 2.4.6 / 4.1.2).
 *
 * This sets the ARIA state only. A literal `required` attribute in the template would activate
 * Angular's RequiredValidator and add Validators.required to the bound control, changing form
 * validation - several controls here use validators that deliberately allow an empty value,
 * such as getStateValidator for countries with no state list.
 */
@Directive({
  selector: '[required-aria]',
  standalone: false
})
export class RequiredAriaDirective implements OnChanges {
  @Input('required-aria') isRequired: boolean;

  /** whether this directive is the thing that made the control required */
  private hasPromoted: boolean = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Optional() @Self() private control: MatFormFieldControl<unknown>
  ) {}

  ngOnChanges(): void {
    const isRequired = !!this.isRequired;

    if (this.control) {
      if (!isRequired && !this.hasPromoted) {
        //never downgrade a control we did not promote. MatInput and MatSelect derive `required`
        //from Validators.required on the bound control when nothing overrides it, and writing
        //false here would clobber that fallback.
        return;
      }

      //MatInput and MatSelect both host-bind attr.aria-required to their own `required`
      //property, so setting the attribute here would just be overwritten. MatFormFieldControl
      //declares `required` readonly, but every concrete control implements it as a settable
      //input, so write through it rather than enumerating control types.
      (this.control as { required: boolean }).required = isRequired;
      this.hasPromoted = isRequired;
      return;
    }

    this.renderer.setAttribute(this.el.nativeElement, 'aria-required', `${isRequired}`);
  }
}
